import React, { useState, useEffect, useCallback } from 'react';
import {
    Container, Typography, Select, MenuItem, Button, Tabs, Tab, Box, Grid, Card, CardContent, CardHeader,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, Tooltip, TextField, Chip,
    SelectChangeEvent
} from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Checkbox from '@mui/material/Checkbox';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    ArcElement,
    Title,
    Tooltip as ChartTooltip, // Renamed to avoid conflict with MUI Tooltip
    Legend,
    TimeScale, // Import TimeScale if needed for date axes
    ChartOptions, // Import ChartOptions type
    ChartData // Import ChartData type
} from 'chart.js';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useApi } from '../api/api';
import { useNavigate } from 'react-router-dom';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    ArcElement,
    Title,
    ChartTooltip,
    Legend,
    TimeScale // Register TimeScale if you use it
);

// Define interfaces for the data structure (based on backend changes)
interface AccuracyMetrics {
    title_accuracy: number;
    type_accuracy: number;
    sub_type_accuracy: number;
    item_accuracy: number;
    priority_accuracy: number;
    recurring_detection_accuracy: number;
    overall_accuracy: number;
    successful_tickets: number;
    tickets_analyzed?: number; // Added this based on backend
    reassign_ratio?: number; // Add re-assign ratio
}

interface AggregationData {
    total: number;
    successful: number;
    success_rate?: number;
}

interface Aggregations {
    by_day: { [key: string]: AggregationData };
    by_company: { [key: string]: AggregationData };
    by_engineer: { [key: string]: AggregationData };
    by_type: { [key: string]: AggregationData };
    by_sub_type: { [key: string]: AggregationData };
    by_item: { [key: string]: AggregationData };
    by_assignee: { [key: string]: AggregationData };
    by_priority: { [key: string]: AggregationData };
    by_recurring: { [key: string]: AggregationData };
    by_source: { [key: string]: AggregationData }; // Add source distribution
}

interface QualityEvaluation {
    ai_score: number;
    human_score?: number | null;
    explanation: string;
}

interface ScoreComparison {
    ai_score: number;
    human_score: number;
}

interface DetailResult {
    ticket_id: string | number;
    created_date: string;
    date?: string; // date
    is_vip: boolean;
    company: string;
    successful: boolean;
    correct_fields: number;
    board_name: string;
    actual_status: string;
    title: { ai: string; actual: string; match: boolean; quality: QualityEvaluation };
    type: { ai: string; actual: string; match: boolean; scores: ScoreComparison };
    sub_type: { ai: string; actual: string; match: boolean; scores: ScoreComparison };
    item: { ai: string; actual: string; match: boolean; scores: ScoreComparison };
    priority: { ai: string; actual: string; match: boolean; quality: QualityEvaluation };
    assignee: { 
        ai: string; 
        actual: string; 
        match: boolean; 
        quality: {
            ai_score: number;
            human_score: number;
            explanation: string;
            dispatch_rules_score: number;
            expertise_score: number;
            priority_score: number;
        }
    };
    recurring: { ai: boolean; detected: boolean };
}

interface DashboardData {
    total_tickets_processed: number;
    tickets_analyzed: number;
    metrics: {
        total_tickets: number;
        successful_tickets: number;
        overall_accuracy: number;
        title_accuracy: number;
        type_accuracy: number;
        sub_type_accuracy: number;
        item_accuracy: number;
        priority_accuracy: number;
        recurring_detection_accuracy: number;
        reassign_ratio?: number; // Add re-assign ratio
    };
    aggregations: Aggregations;
    detailed_results: DetailResult[];
}

// Add interface for EngineerSkill
interface EngineerSkill {
    id: number;
    engineer_name: string;
    type_name: string;
    subtype_name: string;
    item_name: string;
    ticket_count: number;
    average_time_spent: number;
    is_ignore: boolean;
}

const AIDispatcherDashboard: React.FC = () => {
    const [data, setData] = useState<DashboardData | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    
    // Replace single days dropdown with start and end date
    const [startDate, setStartDate] = useState<string>(''); // Format: YYYY-MM-DD
    const [endDate, setEndDate] = useState<string>('');     // Format: YYYY-MM-DD
    
    // Update ticket limit to include "All" option (value 0)
    const [limit, setLimit] = useState<number>(100);
    const [currentTab, setCurrentTab] = useState<number>(0);
    const [searchTerm, setSearchTerm] = useState<string>('');

    // --- Skill Set Tab State ---
    const [engineerSkills, setEngineerSkills] = useState<any[]>([]);
    const [loadingSkills, setLoadingSkills] = useState<boolean>(false);
    const [skillsError, setSkillsError] = useState<string | null>(null);
    const [ignoreEdits, setIgnoreEdits] = useState<{[id: number]: boolean}>({});
    const [savingIgnore, setSavingIgnore] = useState(false);
    const [saveResult, setSaveResult] = useState<{success: boolean, message: string}|null>(null);
    // Filters for skill set table
    const [engineerFilter, setEngineerFilter] = useState<string[]>([]);
    const [typeFilter, setTypeFilter] = useState<string[]>([]);
    const [subTypeFilter, setSubTypeFilter] = useState<string[]>([]);
    const [itemFilter, setItemFilter] = useState<string[]>([]);
    // Add pagination state
    const [skillsPage, setSkillsPage] = useState<number>(0);
    const [skillsRowsPerPage, setSkillsRowsPerPage] = useState<number>(50);
    const [totalSkillsCount, setTotalSkillsCount] = useState<number>(0);

    // --- Engineer-Priority Links Tab State ---
    const [priorityLinks, setPriorityLinks] = useState<any[]>([]);
    const [loadingLinks, setLoadingLinks] = useState<boolean>(false);
    const [linksError, setLinksError] = useState<string | null>(null);
    const [canHandleEdits, setCanHandleEdits] = useState<{[key: string]: boolean}>({});
    const [savingLinks, setSavingLinks] = useState(false);
    const [saveLinksResult, setSaveLinksResult] = useState<{success: boolean, message: string}|null>(null);
    // Filters for priority links table
    const [priorityEngineerFilter, setPriorityEngineerFilter] = useState<string[]>([]);
    const [priorityFilter, setPriorityFilter] = useState<string[]>([]);

    // Add state for filter options
    const [filterOptions, setFilterOptions] = useState<{
        engineers: string[];
        types: string[];
        subtypes: string[];
        items: string[];
    }>({
        engineers: [],
        types: [],
        subtypes: [],
        items: []
    });

    // Use authenticated API client
    const { fetchWithAuth } = useApi();
    const navigate = useNavigate();

    // Get current date and date 7 days ago for default values
    useEffect(() => {
        const today = new Date();
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(today.getDate() - 7);
        
        // Format dates as YYYY-MM-DD
        setEndDate(formatDateForInput(today));
        setStartDate(formatDateForInput(sevenDaysAgo));
    }, []);

    // Helper function to format dates for input fields
    const formatDateForInput = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Calculate days between dates or use default value
    const calculateDaysBetween = (start: string, end: string): number => {
        if (!start || !end) return 7; // Default to 7 days if dates are not set
        
        const startDt = new Date(start);
        const endDt = new Date(end);
        const diffTime = Math.abs(endDt.getTime() - startDt.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        return diffDays || 1; // Ensure at least 1 day
    };

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        
        // Use 0 for "All" in the API request
        const limitValue = limit === 0 ? 10000 : limit; // Use a high number for "All"

        try {
            // Send start and end dates directly to the API
            const result: DashboardData = await fetchWithAuth(
                `/dashboard/accuracy-data?start_date=${startDate}&end_date=${endDate}&limit=${limitValue}`
            );
            console.log("Fetched data:", result);
            
            // --- Data validation/defaulting ---
            const metrics = result.metrics || {} as AccuracyMetrics;
            const aggregations = result.aggregations || {} as Aggregations;
            const detailed_results = result.detailed_results || [];
            const tickets_analyzed = result.tickets_analyzed || detailed_results.length; // Fallback if missing

            setData({
                ...result, // Keep other top-level fields like total_tickets_processed
                tickets_analyzed: tickets_analyzed,
                metrics: metrics,
                aggregations: {
                    by_day: aggregations.by_day || {},
                    by_company: aggregations.by_company || {},
                    by_engineer: aggregations.by_engineer || {},
                    by_type: aggregations.by_type || {},
                    by_sub_type: aggregations.by_sub_type || {},
                    by_item: aggregations.by_item || {},
                    by_assignee: aggregations.by_assignee || {},
                    by_priority: aggregations.by_priority || {},
                    by_recurring: aggregations.by_recurring || {},
                    by_source: aggregations.by_source || {}
                },
                detailed_results: detailed_results
            });
        } catch (err) {
            console.error('Error fetching dashboard data:', err);
            setError(err instanceof Error ? err.message : 'An unknown error occurred');
            setData(null); // Clear data on error
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate, limit, fetchWithAuth]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setCurrentTab(newValue);
    };

    const handleStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setStartDate(event.target.value);
    };

    const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setEndDate(event.target.value);
    };

    const handleLimitChange = (event: SelectChangeEvent<number>) => {
        setLimit(event.target.value as number);
    };
    
    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };

    // Fetch engineer skills when the tab is selected or when filters/pagination change
    useEffect(() => {
        const fetchEngineerSkills = async () => {
            if (currentTab !== 2 || loadingSkills) return;
            
            setLoadingSkills(true);
            setSkillsError(null);

            try {
                // Create query parameters
                const params = new URLSearchParams({
                    page: skillsPage.toString(),
                    limit: skillsRowsPerPage.toString(),
                    ...(engineerFilter.length > 0 && { engineer: engineerFilter.join(',') }),
                    ...(typeFilter.length > 0 && { type: typeFilter.join(',') }),
                    ...(subTypeFilter.length > 0 && { subtype: subTypeFilter.join(',') }),
                    ...(itemFilter.length > 0 && { item: itemFilter.join(',') })
                });

                const result = await fetchWithAuth(`/dashboard/engineer-skills?${params.toString()}`);
                setEngineerSkills(result.skills || []);
                setTotalSkillsCount(result.total || 0);
            } catch (err) {
                setSkillsError(err instanceof Error ? err.message : 'Failed to load engineer skills');
            } finally {
                setLoadingSkills(false);
            }
        };

        fetchEngineerSkills();
    }, [currentTab, skillsPage, skillsRowsPerPage, engineerFilter, typeFilter, subTypeFilter, itemFilter]);

    // When data loads, reset ignoreEdits
    useEffect(() => {
        setIgnoreEdits({});
    }, [engineerSkills]);

    // Handle checkbox change for is_ignore
    const handleIgnoreChange = (id: number, checked: boolean) => {
        setIgnoreEdits(prev => {
            const newEdits = { ...prev };
            // Only include changed values
            if (checked !== engineerSkills.find((skill: EngineerSkill) => skill.id === id)?.is_ignore) {
                newEdits[id] = checked;
            } else {
                delete newEdits[id];
            }
            return newEdits;
        });
    };

    // Save all changed is_ignore values
    const handleSaveIgnore = async () => {
        setSavingIgnore(true);
        let success = true;
        let errorMsg = '';
        try {
            for (const [idStr, is_ignore] of Object.entries(ignoreEdits)) {
                const id = Number(idStr);
                const resp = await fetchWithAuth('/dashboard/ticket-statistics/ignore', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ ticket_statistics_id: id, is_ignore })
                });
                if (!resp.success) {
                    success = false;
                    errorMsg = resp.error || 'Failed to save some changes.';
                    break;
                }
            }
            if (success) {
                setSaveResult({ success: true, message: 'Saved successfully!' });
                setIgnoreEdits({});
                // Refresh the data with current filters and pagination
                const params = new URLSearchParams({
                    page: skillsPage.toString(),
                    limit: skillsRowsPerPage.toString(),
                    ...(engineerFilter.length > 0 && { engineer: engineerFilter.join(',') }),
                    ...(typeFilter.length > 0 && { type: typeFilter.join(',') }),
                    ...(subTypeFilter.length > 0 && { subtype: subTypeFilter.join(',') }),
                    ...(itemFilter.length > 0 && { item: itemFilter.join(',') })
                });
                const result = await fetchWithAuth(`/dashboard/engineer-skills?${params.toString()}`);
                setEngineerSkills(result.skills || []);
                setTotalSkillsCount(result.total || 0);
            } else {
                setSaveResult({ success: false, message: errorMsg });
            }
        } catch (e) {
            setSaveResult({ success: false, message: 'Error saving changes.' });
        } finally {
            setSavingIgnore(false);
        }
    };

    // Fetch engineer-priority links when the tab is selected
    useEffect(() => {
        if (currentTab === 3 && priorityLinks.length === 0 && !loadingLinks) {
            setLoadingLinks(true);
            setLinksError(null);
            fetchWithAuth('/dashboard/engineer-priority-links')
                .then((result: any) => {
                    setPriorityLinks(result.links || []);
                })
                .catch((err: any) => {
                    setLinksError(err instanceof Error ? err.message : 'Failed to load engineer-priority links');
                })
                .finally(() => setLoadingLinks(false));
        }
    }, [currentTab, fetchWithAuth, priorityLinks.length, loadingLinks]);

    // When data loads, reset canHandleEdits
    useEffect(() => {
        setCanHandleEdits({});
    }, [priorityLinks]);

    const getPriorityLinkKey = (row: any) => `${row.engineer_id}_${row.priority_id}`;
    const handleCanHandleChange = (row: any, checked: boolean) => {
        const key = getPriorityLinkKey(row);
        setCanHandleEdits(prev => ({ ...prev, [key]: checked }));
    };

    const handleSaveLinks = async () => {
        setSavingLinks(true);
        let success = true;
        let errorMsg = '';
        try {
            for (const key of Object.keys(canHandleEdits)) {
                const [engineer_id, priority_id] = key.split('_');
                const can_handle = canHandleEdits[key];
                const resp = await fetchWithAuth('/dashboard/engineer-priority-links/update', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ engineer_id: Number(engineer_id), priority_id: Number(priority_id), can_handle })
                });
                if (!resp.success) {
                    success = false;
                    errorMsg = resp.error || 'Failed to save some changes.';
                }
            }
            if (success) {
                setSaveLinksResult({ success: true, message: 'Saved successfully!' });
                setCanHandleEdits({});
                fetchWithAuth('/dashboard/engineer-priority-links').then((result: any) => {
                    setPriorityLinks(result.links || []);
                });
            } else {
                setSaveLinksResult({ success: false, message: errorMsg });
            }
        } catch (e) {
            setSaveLinksResult({ success: false, message: 'Error saving changes.' });
        } finally {
            setSavingLinks(false);
        }
    };

    // Helper function to generate distribution data for a given aggregation
    const generateDistributionData = (aggregationKey: keyof Aggregations) => {
        if (!data || !data.aggregations[aggregationKey]) {
            console.log(`No aggregation data for ${aggregationKey}`);
            return null;
        }

        const aggregation = data.aggregations[aggregationKey];
        if (Object.keys(aggregation).length === 0) {
            console.log(`Empty aggregation for ${aggregationKey}`);
            return { labels: [], dataValues: [] };
        }

        const labels: string[] = [];
        const dataValues: number[] = [];

        // Sort entries by total value, descending
        const sortedEntries = Object.entries(aggregation)
            .sort((a, b) => b[1].total - a[1].total)
            .slice(0, 10); // Limit to top 10 for readability

        sortedEntries.forEach(([key, value]) => {
            // Check for empty or "Unknown" values
            const displayKey = key === "" ? "Unspecified" : key;
            labels.push(displayKey);
            dataValues.push(value.total);
        });

        console.log(`Generated distribution data for ${aggregationKey}:`, { 
            entryCount: Object.keys(aggregation).length,
            nonZeroCount: sortedEntries.filter(([_, v]) => v.total > 0).length,
            labels, 
            dataValues 
        });

        return { labels, dataValues };
    };

    // Render distribution chart for any aggregation type
    const renderDistributionChart = (title: string, aggregationKey: keyof Aggregations) => {
        const distributionData = generateDistributionData(aggregationKey);
        if (!distributionData) return null;

        const { labels, dataValues } = distributionData;
        
        // Debug log to check data
        console.log(`Distribution data for ${aggregationKey}:`, { labels, dataValues });

        // Skip rendering if no data points with values > 0
        if (dataValues.every(value => value === 0) || labels.length === 0) {
            return (
                <Box sx={{ height: 300, width: '100%', mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography variant="body1" color="text.secondary">No data available</Typography>
                </Box>
            );
        }

        const chartData: ChartData<'doughnut'> = {
            labels: labels,
            datasets: [
                {
                    label: 'Number of Tickets',
                    data: dataValues,
                    backgroundColor: [
                        'rgba(54, 162, 235, 0.6)',
                        'rgba(75, 192, 192, 0.6)',
                        'rgba(255, 159, 64, 0.6)',
                        'rgba(153, 102, 255, 0.6)',
                        'rgba(255, 99, 132, 0.6)',
                        'rgba(201, 203, 207, 0.6)',
                        'rgba(255, 205, 86, 0.6)',
                        'rgba(25, 111, 61, 0.6)',
                        'rgba(142, 68, 173, 0.6)',
                        'rgba(231, 76, 60, 0.6)'
                    ],
                    borderColor: [
                        'rgb(54, 162, 235)',
                        'rgb(75, 192, 192)',
                        'rgb(255, 159, 64)',
                        'rgb(153, 102, 255)',
                        'rgb(255, 99, 132)',
                        'rgb(201, 203, 207)',
                        'rgb(255, 205, 86)',
                        'rgb(25, 111, 61)',
                        'rgb(142, 68, 173)',
                        'rgb(231, 76, 60)'
                    ],
                    borderWidth: 1,
                },
            ],
        };

        const options: ChartOptions<'doughnut'> = {
            plugins: {
                title: {
                    display: true,
                    text: title,
                    font: {
                        size: 16,
                    },
                },
                legend: {
                    display: true,
                    position: 'right',
                },
                tooltip: {
                    callbacks: {
                        label: function(context: any) {
                            const label = context.label || '';
                            const value = context.raw as number;
                            const total = dataValues.reduce((sum, val) => sum + val, 0);
                            const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
                            return `${label}: ${value} (${percentage}%)`;
                        }
                    }
                }
            },
            responsive: true,
            maintainAspectRatio: false,
        };

        return (
            <Box sx={{ height: 300, width: '100%', mb: 2 }}>
                <Doughnut data={chartData} options={options} />
            </Box>
        );
    };

    // Distribution chart rendering functions
    const renderTypeDistribution = () => {
        return renderDistributionChart('Ticket Type Distribution', 'by_type');
    };

    const renderSubTypeDistribution = () => {
        return renderDistributionChart('Ticket Sub-Type Distribution', 'by_sub_type');
    };

    const renderPriorityDistribution = () => {
        return renderDistributionChart('Ticket Priority Distribution', 'by_priority');
    };

    const renderAssigneeDistribution = () => {
        return renderDistributionChart('Ticket Assignee Distribution', 'by_assignee');
    };

    const renderRecurringDistribution = () => {
        return renderDistributionChart('Recurring Status Distribution', 'by_recurring');
    };

    const renderCompanyDistribution = () => {
        return renderDistributionChart('Company Distribution', 'by_company');
    };

    const renderItemDistribution = () => {
        return renderDistributionChart('Item Distribution', 'by_item');
    };

    // Add new chart function for source distribution
    const renderSourceDistribution = () => {
        return renderDistributionChart('Source Distribution', 'by_source');
    };

    // Modified chart functions
    const renderTicketsByDayChart = () => {
        if (!data || !data.aggregations.by_day) return null;

        const byDay = data.aggregations.by_day;
        const labels: string[] = [];
        const ticketCounts: number[] = [];

        // Sort by date
        const sortedDates = Object.keys(byDay).sort();
        
        sortedDates.forEach(date => {
            labels.push(date);
            ticketCounts.push(byDay[date].total);
        });

        const chartData: ChartData<'bar'> = {
            labels: labels,
            datasets: [
                {
                    type: 'bar' as const,
                    label: 'Tickets',
                    data: ticketCounts,
                    backgroundColor: 'rgba(54, 162, 235, 0.6)',
                    borderColor: 'rgba(54, 162, 235, 1)',
                    borderWidth: 1,
                    yAxisID: 'y',
                }
            ],
        };

        const options: ChartOptions<'bar'> = {
            responsive: true,
            plugins: {
                title: {
                    display: true,
                    text: 'Tickets by Day',
                    font: {
                        size: 16,
                    },
                },
            },
            scales: {
                y: {
                    type: 'linear',
                    display: true,
                    position: 'left',
                    title: {
                        display: true,
                        text: 'Number of Tickets',
                    },
                },
            },
        };

        return (
            <Box sx={{ height: 300, width: '100%', mb: 2 }}>
                <Bar data={chartData} options={options} />
            </Box>
        );
    };

    // Modify the renderAggregateChart to remove success rate
    const renderAggregateChart = () => {
        if (!data || !data.metrics) return null;

        const metrics = data.metrics;
        
        // Define colors for the bars
        const backgroundColors = [
            'rgba(54, 162, 235, 0.6)', // Title
            'rgba(75, 192, 192, 0.6)', // Type
            'rgba(255, 159, 64, 0.6)', // Sub-Type
            'rgba(153, 102, 255, 0.6)', // Item
            'rgba(255, 99, 132, 0.6)',  // Priority
            'rgba(201, 203, 207, 0.6)'  // Recurring
        ];
        
        const borderColors = [
            'rgb(54, 162, 235)',
            'rgb(75, 192, 192)',
            'rgb(255, 159, 64)',
            'rgb(153, 102, 255)',
            'rgb(255, 99, 132)',
            'rgb(201, 203, 207)'
        ];

        const chartData: ChartData<'bar'> = { // Change type to 'bar'
            labels: ['Title', 'Type', 'Sub-Type', 'Item', 'Priority', 'Recurring'],
            datasets: [
                {
                    // Remove type: 'line'
                    label: 'Accuracy (%)',
                    data: [
                        metrics.title_accuracy,
                        metrics.type_accuracy,
                        metrics.sub_type_accuracy,
                        metrics.item_accuracy,
                        metrics.priority_accuracy,
                        metrics.recurring_detection_accuracy,
                    ],
                    // Remove fill: true and tension: 0.1
                    backgroundColor: backgroundColors, // Use color arrays
                    borderColor: borderColors, // Use color arrays
                    borderWidth: 1 // Add borderWidth
                },
            ],
        };

        const options: ChartOptions<'bar'> = { // Change type to 'bar'
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                title: {
                    display: true,
                    text: 'Field Accuracy Breakdown', // Update title
                    font: {
                        size: 16,
                    },
                },
                legend: { // Hide legend as colors are per bar
                    display: false,
                }
            },
            scales: {
                y: {
                    beginAtZero: true,
                    max: 100,
                    title: {
                        display: true,
                        text: 'Accuracy (%)',
                    },
                },
                x: {
                    grid: {
                        display: false
                    }
                }
            },
        };

        return (
            <Box sx={{ height: '100%', width: '100%' }}>
                <Bar data={chartData} options={options} /> // Change component to Bar
            </Box>
        );
    };

    // New chart to compare AI and human title quality
    const renderTitleQualityComparisonChart = () => {
        if (!data?.detailed_results || data.detailed_results.length === 0) return null;
        
        console.log("Total tickets in data:", data.detailed_results.length);
        
        // Extract AI scores and human scores for titles
        const aiScores: number[] = [];
        const humanScores: number[] = [];
        const labels: string[] = [];
        
        // Use all tickets instead of first 10
        data.detailed_results.forEach(ticket => {
            if (ticket.title?.quality) {
                aiScores.push(ticket.title.quality.ai_score);
                humanScores.push(ticket.title.quality.human_score || 0);
                // Convert ticket_id to string before using substring
                const ticketIdStr = String(ticket.ticket_id);
                labels.push(ticketIdStr.substring(0, 8)); // Truncate ID for readability
            }
        });
        
        console.log("Title quality chart data:", {
            totalTickets: data.detailed_results.length,
            ticketsWithQuality: aiScores.length,
            labels,
            aiScores,
            humanScores
        });
        
        if (aiScores.length === 0) return null;
        
        // Calculate mean scores
        const aiMean = aiScores.reduce((sum, score) => sum + score, 0) / aiScores.length;
        const humanMean = humanScores.reduce((sum, score) => sum + score, 0) / humanScores.length;
        
        console.log("Title quality mean scores:", { aiMean, humanMean });
        
        const chartData: ChartData<'bar'> = {
            labels: labels,
            datasets: [
                {
                    type: 'bar' as const,
                    label: 'AI Score',
                    data: aiScores,
                    backgroundColor: 'rgba(54, 162, 235, 0.6)',
                    borderColor: 'rgba(54, 162, 235, 1)',
                    borderWidth: 1,
                },
                {
                    type: 'bar' as const,
                    label: 'Human Score',
                    data: humanScores,
                    backgroundColor: 'rgba(255, 99, 132, 0.6)',
                    borderColor: 'rgba(255, 99, 132, 1)',
                    borderWidth: 1,
                }
            ],
        };
        
        const options: ChartOptions<'bar'> = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                title: {
                    display: true,
                    text: 'Title Quality: AI vs Human',
                    font: {
                        size: 16,
                    },
                },
                tooltip: {
                    callbacks: {
                        afterLabel: function(context: {
                            dataIndex: number;
                            dataset: { label: string };
                            parsed: { y: number };
                        }) {
                            const ticketIndex = context.dataIndex;
                            const ticket = data.detailed_results[ticketIndex];
                            if (ticket?.title?.quality?.explanation) {
                                return `Explanation: ${ticket.title.quality.explanation}`;
                            }
                            return '';
                        }
                    }
                }
            },
            scales: {
                x: {
                    ticks: {
                        autoSkip: true,
                        maxRotation: 90,
                        minRotation: 45
                    }
                },
                y: {
                    beginAtZero: true,
                    title: {
                        display: true,
                        text: 'Quality Score',
                    },
                },
            },
        };
        
        return (
            <Box sx={{ height: '100%', width: '100%' }}>
                <Bar data={chartData} options={options} />
            </Box>
        );
    };
    
    // New chart to compare AI and human priority quality
    const renderPriorityAccuracyChart = () => {
        if (!data?.detailed_results || data.detailed_results.length === 0) return null;
        
        console.log("Total tickets for priority chart:", data.detailed_results.length);
        
        // Extract AI scores and human scores for priority
        const aiScores: number[] = [];
        const humanScores: number[] = [];
        const labels: string[] = [];
        const isVip: boolean[] = [];
        
        // Use all tickets instead of first 10
        data.detailed_results.forEach(ticket => {
            if (ticket.priority?.quality) {
                aiScores.push(ticket.priority.quality.ai_score);
                humanScores.push(ticket.priority.quality.human_score || 0);
                // Convert ticket_id to string before using substring
                const ticketIdStr = String(ticket.ticket_id);
                labels.push(ticketIdStr.substring(0, 8)); // Truncate ID for readability
                isVip.push(ticket.is_vip);
            }
        });
        
        console.log("Priority quality chart data:", {
            totalTickets: data.detailed_results.length,
            ticketsWithQuality: aiScores.length,
            vipTickets: isVip.filter(vip => vip).length,
            labels,
            aiScores,
            humanScores
        });
        
        if (aiScores.length === 0) return null;
        
        // Calculate mean scores
        const aiMean = aiScores.reduce((sum, score) => sum + score, 0) / aiScores.length;
        const humanMean = humanScores.reduce((sum, score) => sum + score, 0) / humanScores.length;
        
        console.log("Priority quality mean scores:", { aiMean, humanMean });
        
        const chartData: ChartData<'bar'> = {
            labels: labels,
            datasets: [
                {
                    type: 'bar' as const,
                    label: 'AI Score',
                    data: aiScores,
                    backgroundColor: isVip.map(vip => vip ? 'rgba(255, 159, 64, 0.8)' : 'rgba(54, 162, 235, 0.6)'),
                    borderColor: isVip.map(vip => vip ? 'rgba(255, 159, 64, 1)' : 'rgba(54, 162, 235, 1)'),
                    borderWidth: 1,
                },
                {
                    type: 'bar' as const,
                    label: 'Human Score',
                    data: humanScores,
                    backgroundColor: isVip.map(vip => vip ? 'rgba(255, 99, 132, 0.8)' : 'rgba(255, 99, 132, 0.6)'),
                    borderColor: isVip.map(vip => vip ? 'rgba(255, 99, 132, 1)' : 'rgba(255, 99, 132, 1)'),
                    borderWidth: 1,
                }
            ],
        };
        
        const options: ChartOptions<'bar'> = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                title: {
                    display: true,
                    text: 'Priority Quality: AI vs Human (VIP tickets highlighted)',
                    font: {
                        size: 16,
                    },
                },
                tooltip: {
                    callbacks: {
                        afterLabel: function(context: {
                            dataIndex: number;
                            dataset: { label: string };
                            parsed: { y: number };
                        }) {
                            const ticketIndex = context.dataIndex;
                            const ticket = data.detailed_results[ticketIndex];
                            let message = '';
                            if (ticket.is_vip) {
                                message += `VIP Ticket\n`;
                            }
                            if (ticket?.priority?.quality?.explanation) {
                                message += `Explanation: ${ticket.priority.quality.explanation}`;
                            }
                            return message;
                        }
                    }
                }
            },
            scales: {
                x: {
                    ticks: {
                        autoSkip: true,
                        maxRotation: 90,
                        minRotation: 45
                    }
                },
                y: {
                    beginAtZero: true,
                    title: {
                        display: true,
                        text: 'Quality Score',
                    },
                },
            },
        };
        
        return (
            <Box sx={{ height: '100%', width: '100%' }}>
                <Bar data={chartData} options={options} />
            </Box>
        );
    };
    
    // Calculate the impact of VIP status on priority accuracy
    const calculateVipImpact = () => {
        if (!data?.detailed_results || data.detailed_results.length === 0) return null;
        
        const vipTickets = data.detailed_results.filter(ticket => ticket.is_vip);
        const nonVipTickets = data.detailed_results.filter(ticket => !ticket.is_vip);
        
        let vipCorrect = 0;
        let nonVipCorrect = 0;
        
        vipTickets.forEach(ticket => {
            if (ticket.priority?.match) vipCorrect++;
        });
        
        nonVipTickets.forEach(ticket => {
            if (ticket.priority?.match) nonVipCorrect++;
        });
        
        const vipAccuracy = vipTickets.length > 0 ? (vipCorrect / vipTickets.length * 100).toFixed(1) : 'N/A';
        const nonVipAccuracy = nonVipTickets.length > 0 ? (nonVipCorrect / nonVipTickets.length * 100).toFixed(1) : 'N/A';
        
        return (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography variant="h6" gutterBottom align="center">VIP vs Non-VIP Priority Accuracy</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center', mt: 2 }}>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="warning.main">{vipAccuracy}%</Typography>
                        <Typography variant="body2">VIP Tickets ({vipTickets.length})</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="info.main">{nonVipAccuracy}%</Typography>
                        <Typography variant="body2">Non-VIP Tickets ({nonVipTickets.length})</Typography>
                    </Box>
                </Box>
                <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic', textAlign: 'center' }}>
                    {typeof vipAccuracy === 'string' || typeof nonVipAccuracy === 'string' ? 
                        'Insufficient data to compare' : 
                        parseFloat(vipAccuracy) > parseFloat(nonVipAccuracy) ? 
                            'AI handles VIP tickets more accurately' : 
                            'AI handles standard tickets more accurately'}
                </Typography>
            </Box>
        );
    };

    // New chart to compare AI and human type scores
    const renderTypeComparisonChart = () => {
        if (!data?.detailed_results || data.detailed_results.length === 0) return null;
        
        // Extract AI scores and human scores for types
        const aiScores: number[] = [];
        const humanScores: number[] = [];
        const labels: string[] = [];
        
        // Use all tickets
        data.detailed_results.forEach(ticket => {
            if (ticket.type?.scores) {
                aiScores.push(ticket.type.scores.ai_score);
                humanScores.push(ticket.type.scores.human_score || 0);
                // Convert ticket_id to string before using substring
                const ticketIdStr = String(ticket.ticket_id);
                labels.push(ticketIdStr.substring(0, 8)); // Truncate ID for readability
            }
        });
        
        if (aiScores.length === 0) return null;
        
        // Calculate mean scores
        const aiMean = aiScores.reduce((sum, score) => sum + score, 0) / aiScores.length;
        const humanMean = humanScores.reduce((sum, score) => sum + score, 0) / humanScores.length;
        
        const chartData: ChartData<'bar'> = {
            labels: labels,
            datasets: [
                {
                    type: 'bar' as const,
                    label: 'AI Score',
                    data: aiScores,
                    backgroundColor: 'rgba(54, 162, 235, 0.6)',
                    borderColor: 'rgba(54, 162, 235, 1)',
                    borderWidth: 1,
                },
                {
                    type: 'bar' as const,
                    label: 'Human Score',
                    data: humanScores,
                    backgroundColor: 'rgba(255, 99, 132, 0.6)',
                    borderColor: 'rgba(255, 99, 132, 1)',
                    borderWidth: 1,
                }
            ],
        };
        
        const options: ChartOptions<'bar'> = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                title: {
                    display: true,
                    text: 'Type Selection Quality: AI vs Human',
                    font: {
                        size: 16,
                    },
                },
                tooltip: {
                    callbacks: {
                        afterLabel: function(context: {
                            dataIndex: number;
                            dataset: { label: string };
                            parsed: { y: number };
                        }) {
                            const ticketIndex = context.dataIndex;
                            const ticket = data.detailed_results[ticketIndex];
                            const typeInfo = ticket.type;
                            return `AI: ${typeInfo.ai}\nActual: ${typeInfo.actual}\nMatch: ${typeInfo.match ? 'Yes' : 'No'}`;
                        }
                    }
                }
            },
            scales: {
                x: {
                    ticks: {
                        autoSkip: true,
                        maxRotation: 90,
                        minRotation: 45
                    }
                },
                y: {
                    beginAtZero: true,
                    title: {
                        display: true,
                        text: 'Score',
                    },
                },
            },
        };
        
        return (
            <Box sx={{ height: '100%', width: '100%' }}>
                <Bar data={chartData} options={options} />
            </Box>
        );
    };
    
    // New chart to compare AI and human item scores
    const renderItemComparisonChart = () => {
        if (!data?.detailed_results || data.detailed_results.length === 0) return null;
        
        // Extract AI scores and human scores for items
        const aiScores: number[] = [];
        const humanScores: number[] = [];
        const labels: string[] = [];
        
        // Use all tickets
        data.detailed_results.forEach(ticket => {
            if (ticket.item?.scores) {
                aiScores.push(ticket.item.scores.ai_score);
                humanScores.push(ticket.item.scores.human_score || 0);
                // Convert ticket_id to string before using substring
                const ticketIdStr = String(ticket.ticket_id);
                labels.push(ticketIdStr.substring(0, 8)); // Truncate ID for readability
            }
        });
        
        if (aiScores.length === 0) return null;
        
        // Calculate mean scores
        const aiMean = aiScores.reduce((sum, score) => sum + score, 0) / aiScores.length;
        const humanMean = humanScores.reduce((sum, score) => sum + score, 0) / humanScores.length;
        
        const chartData: ChartData<'bar'> = {
            labels: labels,
            datasets: [
                {
                    type: 'bar' as const,
                    label: 'AI Score',
                    data: aiScores,
                    backgroundColor: 'rgba(54, 162, 235, 0.6)',
                    borderColor: 'rgba(54, 162, 235, 1)',
                    borderWidth: 1,
                },
                {
                    type: 'bar' as const,
                    label: 'Human Score',
                    data: humanScores,
                    backgroundColor: 'rgba(255, 99, 132, 0.6)',
                    borderColor: 'rgba(255, 99, 132, 1)',
                    borderWidth: 1,
                }
            ],
        };
        
        const options: ChartOptions<'bar'> = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                title: {
                    display: true,
                    text: 'Item Selection Quality: AI vs Human',
                    font: {
                        size: 16,
                    },
                },
                tooltip: {
                    callbacks: {
                        afterLabel: function(context: {
                            dataIndex: number;
                            dataset: { label: string };
                            parsed: { y: number };
                        }) {
                            const ticketIndex = context.dataIndex;
                            const ticket = data.detailed_results[ticketIndex];
                            const itemInfo = ticket.item;
                            return `AI: ${itemInfo.ai}\nActual: ${itemInfo.actual}\nMatch: ${itemInfo.match ? 'Yes' : 'No'}`;
                        }
                    }
                }
            },
            scales: {
                x: {
                    ticks: {
                        autoSkip: true,
                        maxRotation: 90,
                        minRotation: 45
                    }
                },
                y: {
                    beginAtZero: true,
                    title: {
                        display: true,
                        text: 'Score',
                    },
                },
            },
        };
        
        return (
            <Box sx={{ height: '100%', width: '100%' }}>
                <Bar data={chartData} options={options} />
            </Box>
        );
    };

    // --- Detail Table Rendering ---
    const renderDetailTable = () => {
        if (!data?.detailed_results) return <TableRow><TableCell colSpan={14} align="center">No data available.</TableCell></TableRow>;

        const filteredResults = data.detailed_results.filter(ticket => {
            const lowerSearch = searchTerm.toLowerCase();
            // Simplified search matching
            const searchableText = `
                ${ticket.ticket_id} 
                ${ticket.title?.ai} ${ticket.title?.actual} 
            `.toLowerCase();
            return searchableText.includes(lowerSearch);
        });
        
        // Sort by date (or other key if extended)
        const sortedResults = [...filteredResults].sort((a, b) => {
            let aVal = '';
            let bVal = '';
            if (sortConfig.key === 'date') {
                aVal = a.date || '';
                bVal = b.date || '';
            } else if (sortConfig.key === 'date') {
                aVal = a.date || '';
                bVal = b.date || '';
            }
            if (!aVal && !bVal) return 0;
            if (!aVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (!bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });

        if (sortedResults.length === 0) {
             return <TableRow><TableCell colSpan={14} align="center">No tickets match your search.</TableCell></TableRow>;
        }

        return sortedResults.map((ticket, index) => {
            const assigneeData = ticket.assignee || {};

            const renderCell = (fieldData: any) => {
                const displayVal = fieldData !== null && fieldData !== undefined ? String(fieldData) : '-';
                // Simple rendering for non-comparison cells
                return <span>{displayVal}</span>; 
            };
            
             const renderComparisonCell = (fieldData: any) => {
                const displayAI = fieldData?.ai !== null && fieldData?.ai !== undefined ? String(fieldData.ai) : '-';
                console.log("fieldData", fieldData);
                 const hasMatch = fieldData && fieldData.match !== undefined;
                 console.log("hasMatch", fieldData.match);
                 const isMatch = hasMatch && fieldData.match;
                 const icon = hasMatch ? (isMatch ? <CheckCircleIcon sx={{ color: 'success.main', fontSize: '1rem', verticalAlign: 'middle', ml: 0.5 }} /> : <CancelIcon sx={{ color: 'error.main', fontSize: '1rem', verticalAlign: 'middle', ml: 0.5 }} />) : null;
                 const tooltipTitle = `AI: ${displayAI}\nActual: ${fieldData?.actual ?? '-'}${fieldData?.quality?.explanation ? `\n\nExplanation:\n${fieldData.quality.explanation}` : ''}${fieldData?.scores ? `\nAI Score: ${fieldData.scores.ai_score}\nHuman Score: ${fieldData.scores.human_score}` : ''}`;
                 
                return (
                     <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{tooltipTitle}</span>} arrow>
                         {/* Wrap content in a span for Tooltip to attach correctly */} 
                         <span>{displayAI}{icon}</span>
                    </Tooltip>
                 );
             };

            const renderAssigneeCell = (fieldData: any) => {
                if (!fieldData) return <TableCell>-</TableCell>;

                const quality = fieldData.quality || {};
                console.log("quality", quality);
                const isGoodMatch = (quality.ai_score || 0) >= (quality.human_score || 0);
                const icon = isGoodMatch ? 
                    <CheckCircleIcon sx={{ color: 'success.main', fontSize: '1rem' }} /> : 
                    <CancelIcon sx={{ color: 'error.main', fontSize: '1rem' }} />;

                const tooltipContent = `AI: ${fieldData.ai || '-'}
Actual: ${fieldData.actual || '-'}

Scores:
AI Score: ${quality.ai_score || 0}/10
Human Score: ${quality.human_score || 0}/10
Dispatch Rules: ${quality.dispatch_rules_score || 0}/10
Expertise: ${quality.expertise_score || 0}/3
Priority: ${quality.priority_score || 0}/2

Explanation:
${quality.explanation || 'No explanation available'}`;

                return (
                    <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{tooltipContent}</span>} arrow>
                        <span>{fieldData.ai || '-'}{icon}</span>
                    </Tooltip>
                );
            };

            return (
                <TableRow key={ticket.ticket_id} hover>
                    <TableCell>{ticket.ticket_id}</TableCell>
                    <TableCell>{ticket.created_date ? new Date(ticket.created_date).toLocaleDateString('en-NZ').slice(0, 10) : '-'}</TableCell>
                    <TableCell>{ticket.date ? new Date(ticket.date).toLocaleDateString('en-NZ').slice(0, 10) : '-'}</TableCell>
                    <TableCell>{renderComparisonCell(ticket.title)}</TableCell>
                    <TableCell>{renderCell(ticket.title?.actual)}</TableCell>
                    <TableCell>{renderComparisonCell(ticket.type)}</TableCell>
                    <TableCell>{renderCell(ticket.type?.actual)}</TableCell>
                    <TableCell>{renderComparisonCell(ticket.sub_type)}</TableCell>
                    <TableCell>{renderCell(ticket.sub_type?.actual)}</TableCell>
                    <TableCell>{renderComparisonCell(ticket.item)}</TableCell>
                    <TableCell>{renderCell(ticket.item?.actual)}</TableCell>
                    <TableCell>{renderComparisonCell(ticket.priority)}</TableCell>
                    <TableCell>{renderCell(ticket.priority?.actual)}</TableCell>
                    <TableCell>{renderComparisonCell(ticket.assignee)}</TableCell>
                    <TableCell>{renderCell(ticket.assignee?.actual)}</TableCell>
                    <TableCell>
                        <Chip 
                            label={`${ticket.successful ? 'Pass' : 'Fail'} (${ticket.correct_fields}/6)`}
                            color={ticket.successful ? 'success' : 'warning'} 
                            size="small"
                        />
                    </TableCell>
                </TableRow>
            );
        });
    };

    // Unique values for dropdowns
    const engineerOptions = Array.from(new Set(engineerSkills.map(row => row.engineer_name))).sort();
    const typeOptions = Array.from(new Set(engineerSkills.map(row => row.type_name))).sort();
    const subTypeOptions = Array.from(new Set(engineerSkills.map(row => row.subtype_name))).sort();
    const itemOptions = Array.from(new Set(engineerSkills.map(row => row.item_name))).sort();

    // Multi-select filter change handlers with 'All' logic
    const handleMultiSelect = (setter: (v: string[]) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        let arr = typeof value === 'string' ? value.split(',') : value;
        if (arr.includes('All')) {
            setter([]); // 'All' means no filter
        } else {
            setter(arr.filter((v: string) => v !== 'All'));
        }
        setSkillsPage(0); // Reset to first page when filters change
    };

    // Unique values for priority links filters
    const priorityEngineerOptions = Array.from(new Set(priorityLinks.map(row => row.engineer_name))).sort();
    const priorityOptions = Array.from(new Set(priorityLinks.map(row => row.priority_name))).sort();
    // Filtered rows for priority links
    const filteredPriorityLinks = priorityLinks.filter(row =>
        (priorityEngineerFilter.length === 0 || priorityEngineerFilter.includes(row.engineer_name)) &&
        (priorityFilter.length === 0 || priorityFilter.includes(row.priority_name))
    );
    // Multi-select filter change handlers with 'All' logic for priority links
    const handlePriorityMultiSelect = (setter: (v: string[]) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        let arr = typeof value === 'string' ? value.split(',') : value;
        if (arr.includes('All')) {
            setter([]); // 'All' means no filter
        } else {
            setter(arr.filter((v: string) => v !== 'All'));
        }
    };

    // Add pagination handlers
    const handleSkillsPageChange = (event: unknown, newPage: number) => {
        setSkillsPage(newPage);
    };

    const handleSkillsRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSkillsRowsPerPage(parseInt(event.target.value, 10));
        setSkillsPage(0);
    };

    // Fetch filter options when the tab is opened
    useEffect(() => {
        const fetchFilterOptions = async () => {
            if (currentTab !== 2) return;
            
            try {
                const result = await fetchWithAuth('/dashboard/engineer-skills-filters');
                setFilterOptions(result);
            } catch (err) {
                console.error('Error fetching filter options:', err);
            }
        };

        fetchFilterOptions();
    }, [currentTab]);

    // New chart to compare AI and human assignee quality
    const renderAssigneeQualityChart = () => {
        if (!data?.detailed_results || data.detailed_results.length === 0) return null;
        
        console.log("Total tickets for assignee chart:", data.detailed_results.length);
        
        // Extract AI scores and human scores for assignee
        const aiScores: number[] = [];
        const humanScores: number[] = [];
        const labels: string[] = [];
        
        // Use all tickets
        data.detailed_results.forEach(ticket => {
            if (ticket.assignee) {
                // Binary score: 1 for match, 0 for no match
                aiScores.push(ticket.assignee.match ? 1 : 0);
                humanScores.push(ticket.assignee.quality?.human_score || 0);
                // Convert ticket_id to string before using substring
                const ticketIdStr = String(ticket.ticket_id);
                labels.push(ticketIdStr.substring(0, 8)); // Truncate ID for readability
            }
        });
        
        if (aiScores.length === 0) return null;
        
        // Calculate mean scores
        const aiMean = aiScores.reduce((sum, score) => sum + score, 0) / aiScores.length;
        const humanMean = humanScores.reduce((sum, score) => sum + score, 0) / humanScores.length;
        
        const chartData: ChartData<'bar'> = {
            labels: labels,
            datasets: [
                {
                    type: 'bar' as const,
                    label: 'AI Score',
                    data: aiScores,
                    backgroundColor: 'rgba(54, 162, 235, 0.6)',
                    borderColor: 'rgba(54, 162, 235, 1)',
                    borderWidth: 1,
                },
                {
                    type: 'bar' as const,
                    label: 'Human Score',
                    data: humanScores,
                    backgroundColor: 'rgba(255, 99, 132, 0.6)',
                    borderColor: 'rgba(255, 99, 132, 1)',
                    borderWidth: 1,
                }
            ],
        };
        
        const options: ChartOptions<'bar'> = {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                title: {
                    display: true,
                    text: 'Assignee Quality: AI vs Human',
                    font: {
                        size: 16,
                    },
                },
                tooltip: {
                    callbacks: {
                        afterLabel: function(context: {
                            dataIndex: number;
                            dataset: { label: string };
                            parsed: { y: number };
                        }) {
                            const ticketIndex = context.dataIndex;
                            const ticket = data.detailed_results[ticketIndex];
                            if (ticket?.assignee) {
                                return [
                                    `AI Assignee: ${ticket.assignee.ai}`,
                                    `Actual Assignee: ${ticket.assignee.actual}`,
                                    `Match: ${ticket.assignee.match ? 'Yes' : 'No'}`
                                ];
                            }
                            return '';
                        }
                    }
                }
            },
            scales: {
                x: {
                    ticks: {
                        autoSkip: true,
                        maxRotation: 90,
                        minRotation: 45
                    }
                },
                y: {
                    type: 'linear',
                    display: true,
                    title: {
                        display: true,
                        text: 'Score',
                    },
                    min: 0,
                    max: 1,
                    ticks: {
                        stepSize: 1
                    }
                }
            },
        };
        
        return (
            <Box sx={{ height: '100%', width: '100%' }}>
                <Bar data={chartData} options={options} />
            </Box>
        );
    };

    // Add state for sorting
    const [sortConfig, setSortConfig] = useState<{ key: string, direction: 'asc' | 'desc' }>({ key: 'date', direction: 'desc' });

    const handleSort = (key: string) => {
        setSortConfig(prev => {
            if (prev.key === key) {
                // Toggle direction
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key, direction: 'asc' };
        });
    };

    // --- Main Render --- 
    return (
        <Container maxWidth="xl" sx={{ mt: 4 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
                <Typography variant="h4" gutterBottom>AI Dispatcher Accuracy Dashboard</Typography>
                <Button variant="outlined" onClick={() => navigate('/dispatch-control-center')}>
                    AI Dispatch Control Center
                </Button>
            </Box>
            
             {/* Filters */}
            <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                 {/* Date range replaced with start and end date pickers */}
                 <Box sx={{ minWidth: 150 }}>
                     <Typography variant="caption" display="block" gutterBottom>Start Date</Typography>
                     <TextField
                         type="date"
                         value={startDate}
                         onChange={handleStartDateChange}
                         fullWidth
                         size="small"
                         InputLabelProps={{ shrink: true }}
                     />
                 </Box>
                 <Box sx={{ minWidth: 150 }}>
                     <Typography variant="caption" display="block" gutterBottom>End Date</Typography>
                     <TextField
                         type="date"
                         value={endDate}
                         onChange={handleEndDateChange}
                         fullWidth
                         size="small"
                         InputLabelProps={{ shrink: true }}
                     />
                 </Box>
                 <Box sx={{ minWidth: 150 }}>
                      <Typography variant="caption" display="block" gutterBottom>Ticket Limit</Typography>
                     <Select value={limit} onChange={handleLimitChange} size="small" fullWidth>
                         <MenuItem value={0}>All</MenuItem>
                         <MenuItem value={50}>50 tickets</MenuItem>
                         <MenuItem value={100}>100 tickets</MenuItem>
                         <MenuItem value={200}>200 tickets</MenuItem>
                         <MenuItem value={500}>500 tickets</MenuItem>
                     </Select>
                 </Box>
                 <Button 
                    variant="contained" 
                    onClick={() => fetchData()} 
                    disabled={loading}
                    size="medium"
                    color="primary"
                    sx={{ minWidth: '100px', height: '40px', zIndex: 1 }}
                 >
                    {loading ? 'Loading...' : 'Analyze'}
                 </Button>
             </Paper>

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={currentTab} onChange={handleTabChange} aria-label="dashboard tabs">
                    <Tab label="Analyze and Accuracy" id="overall-tab" aria-controls="overall-content" />
                    <Tab label="Detail Comparison" id="detail-tab" aria-controls="detail-content" />
                    <Tab label="Engineer Skill Set" id="skills-tab" aria-controls="skills-content" />
                    <Tab label="Engineer-Priority Links" id="priority-links-tab" aria-controls="priority-links-content" />
                </Tabs>
            </Box>

             {/* Loading Indicator - Only show when actively loading */} 
             {loading && <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}><CircularProgress /></Box>}
             
             {/* Error Message */} 
             {error && <Typography color="error" align="center">Failed to load data: {error}</Typography>}

             {/* Initial state - prompt user to click Analyze */}
             {!loading && !error && !data && (
                <Box sx={{ textAlign: 'center', my: 10 }}>
                    <Typography variant="h6" color="text.secondary">
                        Select filters and click "Analyze" to view dashboard data
                    </Typography>
                </Box>
             )}

             {/* Tab Content - Only show when data is loaded */} 
             {!loading && !error && data && (
                 <Box>
                     {/* Overall Tab */} 
                     <Box role="tabpanel" hidden={currentTab !== 0} id="overall-content" aria-labelledby="overall-tab">
                         {currentTab === 0 && (
                             <>
                                {/* Section 1: Analyze the ticket */}
                                <Typography variant="h5" gutterBottom sx={{ mt: 2, mb: 3, fontWeight: 'bold', borderBottom: '2px solid #1976d2', pb: 1 }}>
                                    Analyze the ticket
                                </Typography>
                                <Grid container spacing={3}>                                 
                                 {/* Main Charts */}
                                 <Grid item xs={12} md={6}><Card><CardHeader title="Tickets Analyzed Per Day" /><CardContent sx={{ height: 350 }}>{renderTicketsByDayChart()}</CardContent></Card></Grid>
                                 <Grid item xs={12} md={6}><Card><CardHeader title="Company Distribution" /><CardContent sx={{ height: 350 }}>{renderCompanyDistribution()}</CardContent></Card></Grid>
                                 {/* Distribution Charts */}
                                 <Grid item xs={12} md={4}><Card><CardHeader title="Assignee Distribution" /><CardContent sx={{ height: 350 }}>{renderAssigneeDistribution()}</CardContent></Card></Grid>
                                 <Grid item xs={12} md={4}><Card><CardHeader title="Priority Distribution" /><CardContent sx={{ height: 350 }}>{renderPriorityDistribution()}</CardContent></Card></Grid>
                                 <Grid item xs={12} md={4}><Card><CardHeader title="Recurring Distribution" /><CardContent sx={{ height: 350 }}>{renderRecurringDistribution()}</CardContent></Card></Grid>
                                 
                                 {/* Distribution Charts - Second Row */}
                                 <Grid item xs={12} md={4}><Card><CardHeader title="Type Distribution" /><CardContent sx={{ height: 350 }}>{renderTypeDistribution()}</CardContent></Card></Grid>
                                 <Grid item xs={12} md={4}><Card><CardHeader title="Sub-Type Distribution" /><CardContent sx={{ height: 350 }}>{renderSubTypeDistribution()}</CardContent></Card></Grid>
                                 <Grid item xs={12} md={4}><Card><CardHeader title="Item Distribution" /><CardContent sx={{ height: 350 }}>{renderItemDistribution()}</CardContent></Card></Grid>
                                 
                                 {/* Add Source Distribution Chart */}
                                 <Grid item xs={12} md={4}><Card><CardHeader title="Source Distribution" /><CardContent sx={{ height: 350 }}>{renderSourceDistribution()}</CardContent></Card></Grid>
                                 </Grid>
                                 
                                 {/* Section 2: Accuracy of the AI Dispatcher */}
                                 <Typography variant="h5" gutterBottom sx={{ mt: 5, mb: 3, fontWeight: 'bold', borderBottom: '2px solid #1976d2', pb: 1 }}>
                                    Accuracy of the AI Dispatcher
                                 </Typography>
                                 
                                 {/* Accuracy Metrics in a single row */}
                                 <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 2, mb: 2 }}>
                                     <Tooltip title="Percentage of tickets where at least 4 out of 5 fields were correctly assigned by AI" arrow placement="top">
                                         <Card sx={{ bgcolor: 'primary.main', color: 'white', minWidth: 180 }}>
                                             <CardContent sx={{ p: 2 }}>
                                                 <Typography variant="h4">{data.metrics?.overall_accuracy?.toFixed(1) || '-'}%</Typography>
                                                 <Typography variant="body1">Overall Acc.</Typography>
                                             </CardContent>
                                         </Card>
                                     </Tooltip>
                                     <Tooltip title="Number of tickets where AI successfully assigned at least 4 out of 5 fields correctly" arrow placement="top">
                                         <Card sx={{ bgcolor: 'success.main', color: 'white', minWidth: 180 }}>
                                             <CardContent sx={{ p: 2 }}>
                                                 <Typography variant="h4">{data.metrics?.successful_tickets ?? '-'}</Typography>
                                                 <Typography variant="body1">Successful</Typography>
                                             </CardContent>
                                         </Card>
                                     </Tooltip>
                                     <Tooltip title="Total number of tickets included in the analysis" arrow placement="top">
                                         <Card sx={{ bgcolor: 'info.main', color: 'white', minWidth: 180 }}>
                                             <CardContent sx={{ p: 2 }}>
                                                 <Typography variant="h4">{data.tickets_analyzed ?? '-'}</Typography>
                                                 <Typography variant="body1">Analyzed</Typography>
                                             </CardContent>
                                         </Card>
                                     </Tooltip>
                                     <Tooltip title="Percentage of tickets where AI assigned the correct title" arrow placement="top">
                                         <Card sx={{ bgcolor: 'secondary.main', color: 'white', minWidth: 180 }}>
                                             <CardContent sx={{ p: 2 }}>
                                                 <Typography variant="h4">{data.metrics?.title_accuracy?.toFixed(1) || '-'}%</Typography>
                                                 <Typography variant="body1">Title Acc.</Typography>
                                             </CardContent>
                                         </Card>
                                     </Tooltip>
                                     <Tooltip title="Percentage of tickets where AI assigned the correct priority level" arrow placement="top">
                                         <Card sx={{ bgcolor: 'warning.main', color: 'white', minWidth: 180 }}>
                                             <CardContent sx={{ p: 2 }}>
                                                 <Typography variant="h4">{data.metrics?.priority_accuracy?.toFixed(1) || '-'}%</Typography>
                                                 <Typography variant="body1">Priority Acc.</Typography>
                                             </CardContent>
                                         </Card>
                                     </Tooltip>
                                     <Tooltip title="Percentage of tickets where AI assigned the correct ticket type" arrow placement="top">
                                         <Card sx={{ bgcolor: 'error.main', color: 'white', minWidth: 180 }}>
                                             <CardContent sx={{ p: 2 }}>
                                                 <Typography variant="h4">{data.metrics?.type_accuracy?.toFixed(1) || '-'}%</Typography>
                                                 <Typography variant="body1">Type Acc.</Typography>
                                             </CardContent>
                                         </Card>
                                     </Tooltip>
                                     <Tooltip title="Percentage of tickets where AI assigned the correct ticket sub-type" arrow placement="top">
                                         <Card sx={{ bgcolor: 'info.dark', color: 'white', minWidth: 180 }}>
                                             <CardContent sx={{ p: 2 }}>
                                                 <Typography variant="h4">{data.metrics?.sub_type_accuracy?.toFixed(1) || '-'}%</Typography>
                                                 <Typography variant="body1">SubType Acc.</Typography>
                                             </CardContent>
                                         </Card>
                                     </Tooltip>
                                     <Tooltip title="Percentage of tickets where the AI assignee is different from the actual assignee (re-assigned)" arrow placement="top">
                                         <Card sx={{ bgcolor: 'error.dark', color: 'white', minWidth: 180 }}>
                                             <CardContent sx={{ p: 2 }}>
                                                 <Typography variant="h4">{data.metrics?.reassign_ratio?.toFixed(1) || '-'}%</Typography>
                                                 <Typography variant="body1">Re-Assign Ratio</Typography>
                                             </CardContent>
                                         </Card>
                                     </Tooltip>
                                 </Box>
                                 
                                 {/* Remaining charts in Grid */}
                                 <Grid container spacing={3} sx={{ mb: 4 }}>
                                    {/* Field Accuracy */}
                                    <Grid item xs={12} md={9} sx={{ flexGrow: 3 }}>
                                      <Card sx={{ height: '100%', borderLeft: '4px solid #1976d2' }}>
                                        <CardHeader 
                                            title="Field Accuracy Breakdown" 
                                            subheader="Shows accuracy percentage for each field the AI assigns. Higher values indicate better performance."
                                        />
                                        <CardContent sx={{ height: 350, p: 1 }}>
                                          {renderAggregateChart()}
                                        </CardContent>
                                      </Card>
                                    </Grid>
                                    <Grid item xs={12} md={3} sx={{ flexGrow: 1 }}>
                                      <Card sx={{ height: '100%', borderLeft: '4px solid #f57c00' }}>
                                        <CardHeader 
                                            title="VIP Impact Analysis" 
                                            subheader="Compares AI accuracy for VIP tickets vs. regular tickets"
                                        />
                                        <CardContent sx={{ height: 350 }}>
                                          {calculateVipImpact()}
                                        </CardContent>
                                      </Card>
                                    </Grid>
                                  </Grid>
                                 
                                 {/* Quality Comparison Charts - Full Width */}
                                 <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2 }}>Quality Comparisons</Typography>
                                 
                                 {/* Title Quality Comparison - Full Width */}
                                 <Card sx={{ width: '100%', mb: 3 }}>
                                     <CardHeader 
                                         title="Title Quality Comparison"
                                         subheader="Compares AI title quality scores with human-assigned scores. Higher scores indicate better quality."
                                         action={
                                             data && data.detailed_results && data.detailed_results.length > 0 && (() => {
                                                 const aiScores: number[] = [];
                                                 const humanScores: number[] = [];
                                                 
                                                 // Use all tickets instead of first 10
                                                 data.detailed_results.forEach(ticket => {
                                                     if (ticket.title?.quality) {
                                                         aiScores.push(ticket.title.quality.ai_score);
                                                         humanScores.push(ticket.title.quality.human_score || 0);
                                                     }
                                                 });
                                                 
                                                 if (aiScores.length === 0) return null;
                                                 
                                                 const aiMean = aiScores.reduce((sum, score) => sum + score, 0) / aiScores.length;
                                                 const humanMean = humanScores.reduce((sum, score) => sum + score, 0) / humanScores.length;
                                                 
                                                 console.log("Title quality header mean scores:", { aiMean, humanMean, ticketsWithQuality: aiScores.length });
                                                 
                                                 return (
                                                     <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                         <Typography variant="caption" sx={{ mr: 1, fontWeight: 'bold' }}>Mean Scores:</Typography>
                                                         <Chip size="small" label={`AI: ${aiMean.toFixed(2)}`} color="primary" sx={{ mr: 1 }} />
                                                         <Chip size="small" label={`Human: ${humanMean.toFixed(2)}`} color="error" />
                                                     </Box>
                                                 );
                                             })()
                                         }
                                     />
                                     <CardContent sx={{ height: 300 }}>
                                         {renderTitleQualityComparisonChart()}
                                     </CardContent>
                                 </Card>
                                 
                                 {/* Priority Quality Comparison - Full Width */}
                                 <Card sx={{ width: '100%', mb: 3 }}>
                                     <CardHeader 
                                         title="Priority Quality Comparison"
                                         subheader="Compares AI priority assignment quality with human assignments. VIP tickets are highlighted with orange color."
                                         action={
                                             data && data.detailed_results && data.detailed_results.length > 0 && (() => {
                                                 const aiScores: number[] = [];
                                                 const humanScores: number[] = [];
                                                 
                                                 // Use all tickets instead of first 10
                                                 data.detailed_results.forEach(ticket => {
                                                     if (ticket.priority?.quality) {
                                                         aiScores.push(ticket.priority.quality.ai_score);
                                                         humanScores.push(ticket.priority.quality.human_score || 0);
                                                     }
                                                 });
                                                 
                                                 if (aiScores.length === 0) return null;
                                                 
                                                 const aiMean = aiScores.reduce((sum, score) => sum + score, 0) / aiScores.length;
                                                 const humanMean = humanScores.reduce((sum, score) => sum + score, 0) / humanScores.length;
                                                 
                                                 return (
                                                     <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                         <Typography variant="caption" sx={{ mr: 1, fontWeight: 'bold' }}>Mean Scores:</Typography>
                                                         <Chip size="small" label={`AI: ${aiMean.toFixed(2)}`} color="primary" sx={{ mr: 1 }} />
                                                         <Chip size="small" label={`Human: ${humanMean.toFixed(2)}`} color="error" />
                                                     </Box>
                                                 );
                                             })()
                                         }
                                     />
                                     <CardContent sx={{ height: 300 }}>
                                         {renderPriorityAccuracyChart()}
                                     </CardContent>
                                 </Card>
                                 
                                 {/* Type Comparison - Full Width */}
                                 <Card sx={{ width: '100%', mb: 3 }}>
                                     <CardHeader 
                                         title="Type Quality Comparison"
                                         subheader="Compares how well AI categorizes ticket types compared to human categorization."
                                         action={
                                             data && data.detailed_results && data.detailed_results.length > 0 && (() => {
                                                 const aiScores: number[] = [];
                                                 const humanScores: number[] = [];
                                                 
                                                 data.detailed_results.forEach(ticket => {
                                                     if (ticket.type?.scores) {
                                                         aiScores.push(ticket.type.scores.ai_score);
                                                         humanScores.push(ticket.type.scores.human_score || 0);
                                                     }
                                                 });
                                                 
                                                 if (aiScores.length === 0) return null;
                                                 
                                                 const aiMean = aiScores.reduce((sum, score) => sum + score, 0) / aiScores.length;
                                                 const humanMean = humanScores.reduce((sum, score) => sum + score, 0) / humanScores.length;
                                                 
                                                 return (
                                                     <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                         <Typography variant="caption" sx={{ mr: 1, fontWeight: 'bold' }}>Mean Scores:</Typography>
                                                         <Chip size="small" label={`AI: ${aiMean.toFixed(2)}`} color="primary" sx={{ mr: 1 }} />
                                                         <Chip size="small" label={`Human: ${humanMean.toFixed(2)}`} color="error" />
                                                     </Box>
                                                 );
                                             })()
                                         }
                                     />
                                     <CardContent sx={{ height: 300 }}>
                                         {renderTypeComparisonChart()}
                                     </CardContent>
                                 </Card>
                                 
                                 {/* Item Comparison - Full Width */}
                                 <Card sx={{ width: '100%', mb: 3 }}>
                                     <CardHeader 
                                         title="Item Quality Comparison"
                                         subheader="Shows how accurately AI assigns the correct item compared to human assignment."
                                         action={
                                             data && data.detailed_results && data.detailed_results.length > 0 && (() => {
                                                 const aiScores: number[] = [];
                                                 const humanScores: number[] = [];
                                                 
                                                 data.detailed_results.forEach(ticket => {
                                                     if (ticket.item?.scores) {
                                                         aiScores.push(ticket.item.scores.ai_score);
                                                         humanScores.push(ticket.item.scores.human_score || 0);
                                                     }
                                                 });
                                                 
                                                 if (aiScores.length === 0) return null;
                                                 
                                                 const aiMean = aiScores.reduce((sum, score) => sum + score, 0) / aiScores.length;
                                                 const humanMean = humanScores.reduce((sum, score) => sum + score, 0) / humanScores.length;
                                                 
                                                 return (
                                                     <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                         <Typography variant="caption" sx={{ mr: 1, fontWeight: 'bold' }}>Mean Scores:</Typography>
                                                         <Chip size="small" label={`AI: ${aiMean.toFixed(2)}`} color="primary" sx={{ mr: 1 }} />
                                                         <Chip size="small" label={`Human: ${humanMean.toFixed(2)}`} color="error" />
                                                     </Box>
                                                 );
                                             })()
                                         }
                                     />
                                     <CardContent sx={{ height: 300 }}>
                                         {renderItemComparisonChart()}
                                     </CardContent>
                                 </Card>

                                 {/* Add Assignee Quality Chart after Item Comparison */}
                                 <Card sx={{ width: '100%', mb: 3 }}>
                                     <CardHeader 
                                         title="Assignee Quality Comparison"
                                         subheader="Shows how accurately AI assigns tickets to engineers compared to human assignments."
                                         action={
                                             data && data.detailed_results && data.detailed_results.length > 0 && (() => {
                                                 const aiScores: number[] = [];
                                                 const humanScores: number[] = [];
                                                 
                                                 data.detailed_results.forEach(ticket => {
                                                     if (ticket.assignee) {
                                                         aiScores.push(ticket.assignee.match ? 1 : 0);
                                                         humanScores.push(ticket.assignee.quality?.human_score || 0);
                                                     }
                                                 });
                                                 
                                                 if (aiScores.length === 0) return null;
                                                 
                                                 const aiMean = aiScores.reduce((sum, score) => sum + score, 0) / aiScores.length;
                                                 const humanMean = 1;
                                                 
                                                 return (
                                                     <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                         <Typography variant="caption" sx={{ mr: 1, fontWeight: 'bold' }}>Mean Scores:</Typography>
                                                         <Chip size="small" label={`AI: ${aiMean.toFixed(2)}`} color="primary" sx={{ mr: 1 }} />
                                                         <Chip size="small" label={`Human: ${humanMean.toFixed(2)}`} color="error" />
                                                     </Box>
                                                 );
                                             })()
                                         }
                                     />
                                     <CardContent sx={{ height: 300 }}>
                                         {renderAssigneeQualityChart()}
                                     </CardContent>
                                 </Card>
                             </>
                         )}
                     </Box>
                     
                     {/* Detail Tab */} 
                      <Box role="tabpanel" hidden={currentTab !== 1} id="detail-content" aria-labelledby="detail-tab">
                         {currentTab === 1 && (
                             <Grid container spacing={3}>
                                <Grid item xs={12}>
                                     <Paper sx={{ p: 2, mb: 2 }}>
                                        <TextField 
                                             fullWidth 
                                             label="Search Details (ID, Title)" 
                                             variant="outlined" 
                                             size="small"
                                             value={searchTerm}
                                             onChange={handleSearchChange}
                                         />
                                     </Paper>
                                     <TableContainer component={Paper}>
                                         <Table stickyHeader size="small" aria-label="detailed comparison table">
                                             <TableHead>
                                                 <TableRow>
                                                     <TableCell>ID</TableCell>
                                                     <TableCell>Created Date</TableCell>
                                                     <TableCell
                                                         onClick={() => handleSort('date')}
                                                         style={{ cursor: 'pointer', userSelect: 'none' }}
                                                     >
                                                         Closed Date
                                                         {sortConfig.key === 'date' ? (sortConfig.direction === 'asc' ? ' ▲' : ' ▼') : ''}
                                                     </TableCell>
                                                     <TableCell>AI Title</TableCell>
                                                     <TableCell>Actual Title</TableCell>
                                                     <TableCell>AI Type</TableCell>
                                                     <TableCell>Actual Type</TableCell>
                                                     <TableCell>AI SubType</TableCell>
                                                     <TableCell>Actual SubType</TableCell>
                                                     <TableCell>AI Item</TableCell>
                                                     <TableCell>Actual Item</TableCell>
                                                     <TableCell>AI Priority</TableCell>
                                                     <TableCell>Actual Priority</TableCell>
                                                     <TableCell>AI Assignee</TableCell>
                                                     <TableCell>Actual Assignee</TableCell>
                                                     <TableCell>Overall</TableCell>
                                                 </TableRow>
                                             </TableHead>
                                             <TableBody>
                                                 {renderDetailTable()}
                                             </TableBody>
                                         </Table>
                                     </TableContainer>
                                </Grid>
                             </Grid>
                         )}
                     </Box>

                     {/* Engineer Skill Set Tab */}
                     <Box role="tabpanel" hidden={currentTab !== 2} id="skills-content" aria-labelledby="skills-tab">
                         {currentTab === 2 && (
                             <Grid container spacing={3}>
                                 <Grid item xs={12}>
                                     <Paper sx={{ p: 2, mb: 2 }}>
                                         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                             <Typography variant="h6" gutterBottom>
                                                 Engineer Skill Set (Type, SubType, Item)
                                                 {!loadingSkills && (
                                                     <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                                                         Total: {totalSkillsCount} records
                                                     </Typography>
                                                 )}
                                             </Typography>
                                             <Button 
                                                 variant="contained" 
                                                 color="primary" 
                                                 onClick={handleSaveIgnore} 
                                                 disabled={savingIgnore || Object.keys(ignoreEdits).length === 0}
                                             >
                                                 {savingIgnore ? 'Saving...' : 'Save'}
                                             </Button>
                                         </Box>
                                         
                                         {/* Filters Row */}
                                         <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                                             <TextField
                                                 select
                                                 label="Engineer"
                                                 value={engineerFilter}
                                                 onChange={handleMultiSelect(setEngineerFilter)}
                                                 SelectProps={{ multiple: true, renderValue: (selected: string[]) => selected.length === 0 ? 'All' : selected.join(', ') }}
                                                 size="small"
                                                 sx={{ minWidth: 180 }}
                                             >
                                                 <MenuItem value="All">
                                                     <Checkbox checked={engineerFilter.length === 0} />All
                                                 </MenuItem>
                                                 {filterOptions.engineers.map(name => (
                                                     <MenuItem key={name} value={name}>
                                                         <Checkbox checked={engineerFilter.indexOf(name) > -1} />{name}
                                                     </MenuItem>
                                                 ))}
                                             </TextField>
                                             <TextField
                                                 select
                                                 label="Type"
                                                 value={typeFilter}
                                                 onChange={handleMultiSelect(setTypeFilter)}
                                                 SelectProps={{ multiple: true, renderValue: (selected: string[]) => selected.length === 0 ? 'All' : selected.join(', ') }}
                                                 size="small"
                                                 sx={{ minWidth: 150 }}
                                             >
                                                 <MenuItem value="All">
                                                     <Checkbox checked={typeFilter.length === 0} />All
                                                 </MenuItem>
                                                 {filterOptions.types.map(name => (
                                                     <MenuItem key={name} value={name}>
                                                         <Checkbox checked={typeFilter.indexOf(name) > -1} />{name}
                                                     </MenuItem>
                                                 ))}
                                             </TextField>
                                             <TextField
                                                 select
                                                 label="SubType"
                                                 value={subTypeFilter}
                                                 onChange={handleMultiSelect(setSubTypeFilter)}
                                                 SelectProps={{ multiple: true, renderValue: (selected: string[]) => selected.length === 0 ? 'All' : selected.join(', ') }}
                                                 size="small"
                                                 sx={{ minWidth: 150 }}
                                             >
                                                 <MenuItem value="All">
                                                     <Checkbox checked={subTypeFilter.length === 0} />All
                                                 </MenuItem>
                                                 {filterOptions.subtypes.map(name => (
                                                     <MenuItem key={name} value={name}>
                                                         <Checkbox checked={subTypeFilter.indexOf(name) > -1} />{name}
                                                     </MenuItem>
                                                 ))}
                                             </TextField>
                                             <TextField
                                                 select
                                                 label="Item"
                                                 value={itemFilter}
                                                 onChange={handleMultiSelect(setItemFilter)}
                                                 SelectProps={{ multiple: true, renderValue: (selected: string[]) => selected.length === 0 ? 'All' : selected.join(', ') }}
                                                 size="small"
                                                 sx={{ minWidth: 150 }}
                                             >
                                                 <MenuItem value="All">
                                                     <Checkbox checked={itemFilter.length === 0} />All
                                                 </MenuItem>
                                                 {filterOptions.items.map(name => (
                                                     <MenuItem key={name} value={name}>
                                                         <Checkbox checked={itemFilter.indexOf(name) > -1} />{name}
                                                     </MenuItem>
                                                 ))}
                                             </TextField>
                                         </Box>

                                         {loadingSkills && (
                                             <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                                                 <CircularProgress />
                                             </Box>
                                         )}
                                         
                                         {skillsError && <Typography color="error">{skillsError}</Typography>}
                                         
                                         {!loadingSkills && !skillsError && (
                                             <>
                                                 <TableContainer component={Paper} sx={{ maxHeight: "calc(90vh - 200px)", overflowY: 'auto' }}>
                                                     <Table size="small" stickyHeader>
                                                         <TableHead>
                                                             <TableRow>
                                                                 <TableCell>Engineer</TableCell>
                                                                 <TableCell>Type</TableCell>
                                                                 <TableCell>SubType</TableCell>
                                                                 <TableCell>Item</TableCell>
                                                                 <TableCell>Tickets Handled</TableCell>
                                                                 <TableCell>Avg. Time Spent (hrs)</TableCell>
                                                                 <TableCell>Ignore</TableCell>
                                                             </TableRow>
                                                         </TableHead>
                                                         <TableBody>
                                                             {engineerSkills.length === 0 ? (
                                                                 <TableRow>
                                                                     <TableCell colSpan={7} align="center">No data available.</TableCell>
                                                                 </TableRow>
                                                             ) : (
                                                                 engineerSkills.map((row: EngineerSkill) => {
                                                                     const id = row.id;
                                                                     const checked = id in ignoreEdits ? ignoreEdits[id] : row.is_ignore;
                                                                     return (
                                                                         <TableRow key={id}>
                                                                             <TableCell>{row.engineer_name}</TableCell>
                                                                             <TableCell>{row.type_name}</TableCell>
                                                                             <TableCell>{row.subtype_name}</TableCell>
                                                                             <TableCell>{row.item_name}</TableCell>
                                                                             <TableCell>{row.ticket_count}</TableCell>
                                                                             <TableCell>{row.average_time_spent}</TableCell>
                                                                             <TableCell>
                                                                                 <Checkbox
                                                                                     checked={checked}
                                                                                     onChange={(e) => handleIgnoreChange(id, e.target.checked)}
                                                                                     color="primary"
                                                                                 />
                                                                             </TableCell>
                                                                         </TableRow>
                                                                     );
                                                                 })
                                                             )}
                                                         </TableBody>
                                                     </Table>
                                                 </TableContainer>
                                                 
                                                 <TablePagination
                                                     component="div"
                                                     count={totalSkillsCount}
                                                     page={skillsPage}
                                                     onPageChange={handleSkillsPageChange}
                                                     rowsPerPage={skillsRowsPerPage}
                                                     onRowsPerPageChange={handleSkillsRowsPerPageChange}
                                                     rowsPerPageOptions={[10, 25, 50, 100]}
                                                     sx={{ mt: 2 }}
                                                 />
                                             </>
                                         )}
                                         
                                         <Snackbar open={!!saveResult} autoHideDuration={3000} onClose={() => setSaveResult(null)}>
                                             <Alert onClose={() => setSaveResult(null)} severity={saveResult?.success ? 'success' : 'error'} sx={{ width: '100%' }}>
                                                 {saveResult?.message}
                                             </Alert>
                                         </Snackbar>
                                     </Paper>
                                 </Grid>
                             </Grid>
                         )}
                     </Box>

                     {/* Engineer-Priority Links Tab */}
                     <Box role="tabpanel" hidden={currentTab !== 3} id="priority-links-content" aria-labelledby="priority-links-tab">
                         {currentTab === 3 && (
                             <Grid container spacing={3}>
                                 <Grid item xs={12}>
                                     <Paper sx={{ p: 2, mb: 2 }}>
                                         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                             <Typography variant="h6" gutterBottom>Engineer-Priority Links</Typography>
                                             <Button variant="contained" color="primary" onClick={handleSaveLinks} disabled={savingLinks || Object.keys(canHandleEdits).length === 0}>
                                                 {savingLinks ? 'Saving...' : 'Save'}
                                             </Button>
                                         </Box>
                                         {/* Filters Row */}
                                         <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                                             <TextField
                                                 select
                                                 label="Engineer"
                                                 value={priorityEngineerFilter}
                                                 onChange={handlePriorityMultiSelect(setPriorityEngineerFilter)}
                                                 SelectProps={{ multiple: true, renderValue: (selected: string[]) => selected.length === 0 ? 'All' : selected.join(', ') }}
                                                 size="small"
                                                 sx={{ minWidth: 180 }}
                                             >
                                                 <MenuItem value="All">
                                                     <Checkbox checked={priorityEngineerFilter.length === 0} />All
                                                 </MenuItem>
                                                 {priorityEngineerOptions.map(name => (
                                                     <MenuItem key={name} value={name}>
                                                         <Checkbox checked={priorityEngineerFilter.indexOf(name) > -1} />{name}
                                                     </MenuItem>
                                                 ))}
                                             </TextField>
                                             <TextField
                                                 select
                                                 label="Priority"
                                                 value={priorityFilter}
                                                 onChange={handlePriorityMultiSelect(setPriorityFilter)}
                                                 SelectProps={{ multiple: true, renderValue: (selected: string[]) => selected.length === 0 ? 'All' : selected.join(', ') }}
                                                 size="small"
                                                 sx={{ minWidth: 150 }}
                                             >
                                                 <MenuItem value="All">
                                                     <Checkbox checked={priorityFilter.length === 0} />All
                                                 </MenuItem>
                                                 {priorityOptions.map(name => (
                                                     <MenuItem key={name} value={name}>
                                                         <Checkbox checked={priorityFilter.indexOf(name) > -1} />{name}
                                                     </MenuItem>
                                                 ))}
                                             </TextField>
                                         </Box>
                                         {loadingLinks && <CircularProgress />}
                                         {linksError && <Typography color="error">{linksError}</Typography>}
                                         {!loadingLinks && !linksError && (
                                             <TableContainer component={Paper} sx={{ maxHeight: '90vh', height: '90vh', overflowY: 'auto' }}>
                                                 <Table size="small" stickyHeader>
                                                     <TableHead>
                                                         <TableRow>
                                                             <TableCell>Engineer</TableCell>
                                                             <TableCell>Priority</TableCell>
                                                             <TableCell>Can Handle</TableCell>
                                                         </TableRow>
                                                     </TableHead>
                                                     <TableBody>
                                                         {filteredPriorityLinks.length === 0 ? (
                                                             <TableRow><TableCell colSpan={3} align="center">No data available.</TableCell></TableRow>
                                                         ) : (
                                                             filteredPriorityLinks.map((row) => {
                                                                 const key = getPriorityLinkKey(row);
                                                                 const checked = key in canHandleEdits ? canHandleEdits[key] : row.can_handle;
                                                                 return (
                                                                     <TableRow key={key}>
                                                                         <TableCell>{row.engineer_name}</TableCell>
                                                                         <TableCell>{row.priority_name}</TableCell>
                                                                         <TableCell>
                                                                             <Checkbox
                                                                                 checked={checked}
                                                                                 onChange={e => handleCanHandleChange(row, e.target.checked)}
                                                                                 color="primary"
                                                                             />
                                                                         </TableCell>
                                                                     </TableRow>
                                                                 );
                                                             })
                                                         )}
                                                     </TableBody>
                                                 </Table>
                                             </TableContainer>
                                         )}
                                         <Snackbar open={!!saveLinksResult} autoHideDuration={3000} onClose={() => setSaveLinksResult(null)}>
                                            <Alert onClose={() => setSaveLinksResult(null)} severity={saveLinksResult?.success ? 'success' : 'error'} sx={{ width: '100%' }}>
                                                {saveLinksResult?.message}
                                            </Alert>
                                         </Snackbar>
                                     </Paper>
                                 </Grid>
                             </Grid>
                         )}
                     </Box>
                 </Box>
            )}
        </Container>
    );
};

export default AIDispatcherDashboard; 